import { isPresent } from "@suse-o11y/is-present";
import React from "react";
import { useSelector } from "react-redux";

import { RightPaneState } from "../../../app/state/right-pane/RightPaneState";
import { selectComponentIdentifier } from "../../../app/state/route/RouteSelectors";
import { selectTimestampDate } from "../../../app/state/view/timeline/TimelineSelectors";
import { selectIsExploreSubview } from "../../../app/state/view/viewpoint/ViewPointSelectors";
import { useNavigateFromLink } from "../../../components/react/link";
import { ComponentIcon } from "../../../components/react/states/ComponentIcon";
import { queryFullComponent } from "../../../queries";
import { useQuery } from "../../../queries/react-query";
import { COMPONENT_PATH } from "../../Routes";

import { GenericBreadcrumb } from "./GenericBreadcrumb";
import { SubviewBreadcrumb } from "./SubviewBreadcrumb";

export const ComponentNameBreadcrumb = (): React.ReactNode => {
	const componentIdentifier = useSelector(selectComponentIdentifier);
	const timestamp = useSelector(selectTimestampDate)?.valueOf();
	const isSubview = useSelector(selectIsExploreSubview);
	const linkToComponent = useNavigateFromLink(COMPONENT_PATH, {
		params: (params) => (isSubview ? params : { ...params, mode: undefined }),
		search: (search) => {
			if (!isSubview) {
				return search;
			}

			// reset subview and restore the origin view/query
			return {
				...search,
				explore: undefined,
				view: search.originView,
				query: search.originQuery,
				rightPane: RightPaneState.resetWhenVisible(search.rightPane),
			};
		},
	});

	const {
		isLoading,
		error,
		data: fullComponent,
	} = useQuery({
		keepPreviousData: true,
		enabled: componentIdentifier != null,
		refetchOnTimelineSummaryUpdate: true,

		...queryFullComponent({ componentIdOrIdentifier: componentIdentifier ?? "", timestamp }),
	});

	if (isLoading) {
		return <GenericBreadcrumb icon="" isDirty={false} name="" />;
	}

	if (error || fullComponent == null) {
		return <GenericBreadcrumb icon="" isDirty={false} name="Component not found" />;
	}

	const componentTypeName = fullComponent.typeName;

	const componentName = [fullComponent.data.name, componentTypeName && `(${componentTypeName})`]
		.filter(isPresent)
		.join(" ");

	return (
		<>
			<GenericBreadcrumb
				icon={<ComponentIcon icon={fullComponent.icon} iconSize="tiny" />}
				isDirty={false}
				name={componentName}
				to={linkToComponent}
			/>
			{isSubview && <SubviewBreadcrumb />}
		</>
	);
};

ComponentNameBreadcrumb.PATH = [COMPONENT_PATH];
